import { normalizeReferralCode } from 'utils/referralCode';

/**
 * Body de POST /users/signup/complete para el alta nueva.
 * El nombre es el capturado a mano; el del documento no se envía aquí.
 * @param {object} params
 * @returns {object}
 */
export function buildCompleteSignupPayload({
  user = {},
  otp = {},
  userExtra = {},
  address = {},
  phone = '',
}) {
  const payload = {
    email: user.email,
    phone,
    otp_email: otp.email,
    otp_phone: otp.phone,
    firstName: user.firstName,
    lastName: user.lastName,
    paternal: user.paternal,
    maternal: user.maternal,
    gender: user.gender,
    id_mission: {
      session_id: userExtra.idMission?.sessionId || '',
      request_id: userExtra.idMission?.requestId || '',
      unique_request_id: userExtra.idMission?.uniqueRequestId || '',
      verification_result_id: userExtra.idMission?.verificationResultId || '',
    },
    kyc: {
      job: userExtra.job,
      referral: userExtra.referral,
      education_level: userExtra.educationLevel,
    },
    address: {
      country: 'MX',
      postal_code: address.postalCode,
      neighborhood: address.neighborhood,
      state: address.state,
      municipality: address.city,
      street: address.street,
      exterior_number: address.number,
      interior_number: address.internalNumber,
      prof_of_address: address.document,
    },
    country_of_birth: userExtra.countryOfBirth,
    citizenship: userExtra.citizenship,
  };

  const referralCode = normalizeReferralCode(user.referralCode);
  if (referralCode) {
    payload.referralCode = referralCode;
  }

  return payload;
}

/**
 * Claves camelCase del mapa de error (HTTP 404) y el campo real del formulario.
 * No coinciden con las claves snake_case del body.
 */
export const COMPLETE_ONBOARDING_FIELD_MAP = {
  'kyc.job': 'job',
  'kyc.referral': 'referral',
  'kyc.educationLevel': 'educationLevel',
  'address.country': 'country',
  'address.postalCode': 'postalCode',
  'address.neighborhood': 'neighborhood',
  'address.state': 'state',
  'address.municipality': 'city',
  'address.street': 'street',
  'address.exteriorNumber': 'number',
  'address.interiorNumber': 'internalNumber',
  'address.proofOfAddress': 'document',
  countryOfBirth: 'countryOfBirth',
  citizenship: 'citizenship',
};

/**
 * Body de POST /users/clients/complete-onboarding.
 * Cuenta ya autenticada: no lleva contacto, OTP ni nombre.
 * @param {{ userExtra?: object, address?: object }} params
 * @returns {object}
 */
export function buildCompleteClientOnboardingPayload({
  userExtra = {},
  address = {},
} = {}) {
  return {
    id_mission: {
      session_id: userExtra.idMission?.sessionId || '',
      request_id: userExtra.idMission?.requestId || '',
      unique_request_id: userExtra.idMission?.uniqueRequestId || '',
      verification_result_id: userExtra.idMission?.verificationResultId || '',
    },
    kyc: {
      job: userExtra.job,
      referral: userExtra.referral,
      education_level: userExtra.educationLevel,
    },
    address: {
      country: 'MX',
      postal_code: address.postalCode,
      neighborhood: address.neighborhood,
      state: address.state,
      municipality: address.city,
      street: address.street,
      exterior_number: address.number,
      interior_number: address.internalNumber,
      proof_of_address: address.document,
    },
    country_of_birth: userExtra.countryOfBirth,
    citizenship: userExtra.citizenship,
  };
}

/**
 * Separa el mapa `error` de complete-onboarding.
 * Backend responde 404 con este objeto; no se usa el status para clasificarlo.
 * null cuando `error` no es un objeto de campos.
 * @param {object} error
 * @returns {{ fields: object, hasUnknown: boolean } | null}
 */
export function readCompleteOnboardingFieldErrors(error) {
  const map = error?.data?.error;
  if (!map || typeof map !== 'object' || Array.isArray(map)) {
    return null;
  }
  const fields = {};
  let hasUnknown = false;
  Object.entries(map).forEach(([key, message]) => {
    const field = COMPLETE_ONBOARDING_FIELD_MAP[key];
    if (!field) {
      hasUnknown = true;
      return;
    }
    fields[field] = typeof message === 'string' ? message : '';
  });
  return { fields, hasUnknown };
}

/**
 * Payload de POST /idmission/log. El momento del envío vive en newClientAuthFlow.
 * @param {object} userExtra
 * @returns {object}
 */
export function buildIdMissionLogPayload(userExtra = {}) {
  return {
    session_id: userExtra.idMission?.sessionId || '',
    unique_request_id: userExtra.idMission?.uniqueRequestId || '',
    verification_result_id: userExtra.idMission?.verificationResultId || '',
    verification_result: userExtra.idMission?.message || '',
  };
}
